import { devLog, devError } from '../utils/devLogger';
class BEE123ExportService {
  constructor() {
    this.apiEndpoint = process.env.REACT_APP_BEE123_API_ENDPOINT || 'https://api.bee123.gov.za';
    this.apiKey = process.env.REACT_APP_BEE123_API_KEY || 'YOUR_BEE123_API_KEY';
    this.organizationId = process.env.REACT_APP_BEE123_ORG_ID || 'YOUR_ORG_ID';
    this.isConfigured = this.apiKey !== 'YOUR_BEE123_API_KEY' && this.organizationId !== 'YOUR_ORG_ID';
  }

  checkConfiguration() {
    if (!this.isConfigured) {
      return { 
        success: false, 
        error: 'BEE123 integration is pending final configuration. Please contact your system administrator.' 
      };
    }
    return { success: true };
  }

  async compileLearnerData(learnerId) {
    const configCheck = this.checkConfiguration();
    if (!configCheck.success) {
      return configCheck;
    }

    try {
      const learnerData = await this.getLearnerProfile(learnerId);
      const documents = await this.getLearnerDocuments(learnerId);
      const attendance = await this.getLearnerAttendance(learnerId);
      const progress = await this.getLearnerProgress(learnerId);
      const assessments = await this.getLearnerAssessments(learnerId);

      const compiledData = {
        personalInformation: {
          idNumber: learnerData.idNumber,
          firstName: learnerData.firstName,
          lastName: learnerData.lastName,
          dateOfBirth: learnerData.dateOfBirth,
          gender: learnerData.gender,
          race: learnerData.race,
          disability: learnerData.disability,
          citizenship: learnerData.citizenship,
          homeLanguage: learnerData.homeLanguage,
          contactDetails: {
            phone: learnerData.phone,
            email: learnerData.email,
            address: learnerData.address
          }
        },
        
        trainingInformation: {
          programCode: 'NPI-WRP-001',
          programName: 'Access70 Workplace Readiness Programme',
          nqfLevel: learnerData.nqfLevel || 4,
          credits: learnerData.credits || 120,
          startDate: learnerData.enrollmentDate,
          endDate: learnerData.completionDate,
          providerName: 'Peregrine Systems Technologies',
          providerAccreditation: process.env.REACT_APP_PROVIDER_ACCREDITATION || 'ACC001',
          facilitatorName: learnerData.facilitatorName,
          facilitatorId: learnerData.facilitatorId
        },

        attendanceRecord: attendance.map(record => ({
          date: record.date,
          module: record.moduleId,
          status: record.status,
          hours: record.hours || 8,
          teamsVerified: record.teamsPresent || false
        })),

        assessmentResults: assessments.map(assessment => ({
          assessmentType: assessment.type,
          assessmentDate: assessment.date,
          score: assessment.score,
          result: assessment.result,
          assessorName: assessment.assessorName,
          assessorId: assessment.assessorId
        })),

        competencyAchievement: progress.achievements?.map(achievement => ({
          competencyCode: achievement.competencyCode,
          competencyName: achievement.competencyName,
          achievementDate: achievement.date,
          evidence: achievement.evidence
        })) || [],

        requiredDocuments: this.mapDocumentTypes(documents),

        certificationDetails: {
          certificateNumber: learnerData.certificateNumber,
          issueDate: learnerData.certificateIssueDate,
          expiryDate: learnerData.certificateExpiryDate,
          status: learnerData.certificationStatus
        }
      };

      return { success: true, data: compiledData };
    } catch (error) {
      devError('Failed to compile learner data:', error);
      return { success: false, error: error.message };
    }
  }

  mapDocumentTypes(documents) {
    const documentMapping = {
      'ID': 'identity_document',
      'Qualifications': 'qualification_certificates',
      'Affidavit': 'affidavit_unemployed',
      'EEA1': 'employment_equity_form',
      'BankingDetails': 'banking_details',
      'Consent': 'consent_form',
      'Medical': 'medical_certificate'
    };

    const mappedDocs = {};
    
    documents.forEach(doc => {
      const bee123Type = documentMapping[doc.type];
      if (bee123Type) {
        mappedDocs[bee123Type] = {
          fileName: doc.fileName,
          fileUrl: doc.fileUrl,
          uploadDate: doc.uploadDate,
          verified: doc.status === 'approved',
          verificationDate: doc.verificationDate
        };
      }
    });

    return mappedDocs;
  }

  async gatherRequiredDocuments(learnerId) {
    try {
      const requiredDocTypes = [
        'identity_document',
        'qualification_certificates', 
        'affidavit_unemployed',
        'employment_equity_form',
        'banking_details',
        'consent_form',
        'medical_certificate'
      ];

      const documents = await this.getLearnerDocuments(learnerId);
      const mappedDocs = this.mapDocumentTypes(documents);
      
      const documentStatus = requiredDocTypes.map(docType => ({
        type: docType,
        required: true,
        present: !!mappedDocs[docType],
        verified: mappedDocs[docType]?.verified || false,
        document: mappedDocs[docType] || null
      }));

      const missingDocs = documentStatus.filter(doc => !doc.present);
      const unverifiedDocs = documentStatus.filter(doc => doc.present && !doc.verified);

      return {
        success: true,
        data: {
          documentStatus,
          missingDocuments: missingDocs,
          unverifiedDocuments: unverifiedDocs,
          readyForSubmission: missingDocs.length === 0 && unverifiedDocs.length === 0
        }
      };
    } catch (error) {
      devError('Failed to gather required documents:', error);
      return { success: false, error: error.message };
    }
  }

  async validateSubmissionData(compiledData) {
    try {
      const validationErrors = [];
      const validationWarnings = [];

      if (!compiledData.personalInformation?.idNumber) {
        validationErrors.push('ID Number is required');
      }

      if (!compiledData.personalInformation?.firstName || !compiledData.personalInformation?.lastName) {
        validationErrors.push('First name and last name are required');
      }

      if (!compiledData.trainingInformation?.startDate) {
        validationErrors.push('Training start date is required');
      }

      if (!compiledData.attendanceRecord || compiledData.attendanceRecord.length === 0) {
        validationErrors.push('Attendance records are required');
      }

      const totalTrainingHours = compiledData.attendanceRecord?.reduce((sum, record) => 
        sum + (record.hours || 0), 0) || 0;
      
      if (totalTrainingHours < 120) {
        validationWarnings.push(`Training hours (${totalTrainingHours}) below minimum requirement (120 hours)`);
      }

      const requiredDocs = ['identity_document', 'qualification_certificates', 'employment_equity_form'];
      const missingRequiredDocs = requiredDocs.filter(docType => 
        !compiledData.requiredDocuments?.[docType]?.verified
      );

      if (missingRequiredDocs.length > 0) {
        validationErrors.push(`Missing required documents: ${missingRequiredDocs.join(', ')}`);
      }

      const isValid = validationErrors.length === 0;

      return {
        success: true,
        data: {
          isValid,
          errors: validationErrors,
          warnings: validationWarnings,
          readyForSubmission: isValid && validationWarnings.length === 0
        }
      };
    } catch (error) {
      devError('Failed to validate submission data:', error);
      return { success: false, error: error.message };
    }
  }

  async submitToBEE123(compiledData) {
    try {
      const submissionId = 'SUB_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      
      const submissionPayload = {
        submissionId,
        organizationId: this.organizationId,
        submissionDate: new Date().toISOString(),
        learnerData: compiledData,
        metadata: {
          systemVersion: '1.0.0',
          submittedBy: 'access70-lms',
          submissionType: 'skills_development_levy_claim'
        }
      };

      const response = await fetch(`${this.apiEndpoint}/submissions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`,
          'X-Organization-ID': this.organizationId
        },
        body: JSON.stringify(submissionPayload)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`BEE123 API Error: ${errorData.message || response.statusText}`);
      }

      const result = await response.json();

      const submissionRecord = {
        submissionId,
        learnerId: compiledData.personalInformation.idNumber,
        status: 'submitted',
        submissionDate: new Date().toISOString(),
        bee123Reference: result.referenceNumber,
        estimatedProcessingTime: result.estimatedProcessingDays || 10,
        nextAction: result.nextAction || 'await_processing'
      };

      await this.saveSubmissionRecord(submissionRecord);

      return {
        success: true,
        data: {
          submissionId,
          bee123Reference: result.referenceNumber,
          status: 'submitted',
          message: 'Successfully submitted to BEE123 system'
        }
      };
    } catch (error) {
      devError('Failed to submit to BEE123:', error);
      
      if (error.message.includes('network') || error.message.includes('fetch')) {
        return { 
          success: false, 
          error: 'Network error: Unable to connect to BEE123 system. Please try again later.' 
        };
      }
      
      return { success: false, error: error.message };
    }
  }

  async trackSubmissionStatus(submissionId) {
    try {
      const response = await fetch(`${this.apiEndpoint}/submissions/${submissionId}/status`, {
        headers: {
          'Authorization': `Bearer ${this.apiKey}`,
          'X-Organization-ID': this.organizationId
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to track submission: ${response.statusText}`);
      }

      const statusData = await response.json();

      const submissionStatus = {
        submissionId,
        currentStatus: statusData.status,
        lastUpdated: statusData.lastUpdated,
        processedDocuments: statusData.processedDocuments || 0,
        totalDocuments: statusData.totalDocuments || 0,
        estimatedCompletion: statusData.estimatedCompletion,
        messages: statusData.messages || [],
        nextAction: statusData.nextAction
      };

      await this.updateSubmissionRecord(submissionId, submissionStatus);

      return { success: true, data: submissionStatus };
    } catch (error) {
      devError('Failed to track submission status:', error);
      return { success: false, error: error.message };
    }
  }

  async getSubmissionHistory(learnerId) {
    try {
      const submissions = await this.getStoredSubmissions(learnerId);
      
      return { success: true, data: submissions };
    } catch (error) {
      devError('Failed to get submission history:', error);
      return { success: false, error: error.message };
    }
  }

  async getLearnerProfile(learnerId) {
    return {
      idNumber: '9001015009087',
      firstName: 'John',
      lastName: 'Doe',
      enrollmentDate: '2024-01-15',
      facilitatorName: 'Jane Smith'
    };
  }

  async getLearnerDocuments(learnerId) {
    return [
      { type: 'ID', fileName: 'id_copy.pdf', status: 'approved' },
      { type: 'EEA1', fileName: 'eea1_form.pdf', status: 'pending' }
    ];
  }

  async getLearnerAttendance(learnerId) {
    return [
      { date: '2024-01-15', moduleId: 'MOD001', status: 'present', hours: 8 },
      { date: '2024-01-16', moduleId: 'MOD002', status: 'present', hours: 8 }
    ];
  }

  async getLearnerProgress(learnerId) {
    return {
      achievements: [
        { competencyCode: 'COMP001', competencyName: 'Basic Computing', date: '2024-01-20' }
      ]
    };
  }

  async getLearnerAssessments(learnerId) {
    return [
      { type: 'practical', date: '2024-01-25', score: 85, result: 'competent' }
    ];
  }

  async saveSubmissionRecord(record) {
    devLog('Saving submission record:', record);
  }

  async updateSubmissionRecord(submissionId, updates) {
    devLog('Updating submission record:', submissionId, updates);
  }

  async getStoredSubmissions(learnerId) {
    return [];
  }
}

export default new BEE123ExportService();